import dayjs from 'dayjs';
import { WTS_MOMENT_DATE_MASK, WTS_MOMENT_SHORT_DATE_MASK } from 'src/constants/dayjs';
import utils from 'src/utils';
import { getContractTypeName, getPrimaryContractRow } from 'src/helpers/modular/employeesContractsUtil';
import { shiftsParser } from './CalendarShiftsParser';
export default class VirtualEventsTable {
    period; // TimeSpans like 2025-01-01T00:00:00, 2025-01-02T00:00:00, ... with requested dates range
    initialRequest;
    totalHours;
    rows;
    constructor(initialRequest, inputData) {
        const { dataWithoutSlipTimes } = inputData;
        this.initialRequest = initialRequest;
        this.period = this.getPeriod();
        this.totalHours = this.parseTotalHours(dataWithoutSlipTimes);
        this.rows = this.parsePersons(inputData);
    }
    parseTotalHours(data) {
        const totalHours = {};
        this.period.forEach((timeStamp) => {
            totalHours[timeStamp] = {
                Total: this.getTimeSpan(data.ActualWorkTotals?.Spans?.[timeStamp]),
                TotalFlag1: this.getTimeSpan(data.ActualOffScheduleWorkTotals?.Spans?.[timeStamp])
            };
        });
        return totalHours;
    }
    getPeriod() {
        const { From, To } = this.initialRequest;
        const period = [`${dayjs(From).format(WTS_MOMENT_SHORT_DATE_MASK)}T00:00:00`];
        if (From < To) {
            const end = dayjs(To);
            let candidate = dayjs(period[period.length - 1]).add(1, 'day');
            while (candidate.isBefore(end, 'd')) { // is exclusive
                period.push(candidate.format(WTS_MOMENT_DATE_MASK));
                candidate = candidate.add(1, 'day');
            }
        }
        return period;
    }
    parsePersons(inputData) {
        const rows = [];
        const mainDataSource = inputData.dataWithSlipTimes || inputData.dataWithoutSlipTimes;
        mainDataSource.Persons?.forEach((person, index) => {
            const workedData = inputData.dataWithoutSlipTimes.Persons?.[index] || {};
            const calculatedData = inputData.dataWithSlipTimes?.Persons?.[index] || {};
            const plannedEvents = this.parsePlannedEvents(person); // Planned
            const calendarEventsRaw = [...(workedData?.ActualVirtualEvents || []), ...(workedData.ActualUnfinishedEvents || [])];
            const calendarEvents = this.parseCalendarEvents(calendarEventsRaw); // Worked
            const calculatedEvents = this.parseCalendarEvents(calculatedData?.ActualVirtualWork || []); // Calculated
            const row = {
                User: this.getUserInfo(person),
                CalendarEvents: calendarEvents,
                PlannedEvents: plannedEvents,
                Jobs: this.parseJobs(plannedEvents, calendarEvents, calculatedEvents),
                Errors: this.parseErrors(),
                HasErrors: false,
                Totals: this.parsePersonTotals(workedData, calculatedData),
                UserErrors: [] // Not provided
            };
            row.Department = {
                Id: row.User.DepartmentId ?? null,
                Name: row.User.DepartmentName ?? null
            };
            rows.push(row);
        });
        const withShifts = shiftsParser.parseUsersPeriods(rows);
        return withShifts;
    }
    getUserInfo(data) {
        const { contracts, activeContract } = this.parseUserContracts(data);
        const primaryContractRow = activeContract ? getPrimaryContractRow(activeContract) : null;
        return {
            UserId: data.Id,
            DepartmentId: primaryContractRow?.department?.departmentId ?? null,
            DepartmentName: primaryContractRow?.department?.departmentName ?? null,
            LastName: data.Lastname,
            FirstName: data.Firstname,
            ExternalCode: data.ExternalCode ?? undefined,
            Contracts: contracts,
            MainProfession: primaryContractRow?.Profession?.Name ?? null,
            // @ts-ignore
            ActiveContractTypeName: activeContract?.ContractTypeId ? getContractTypeName(activeContract?.ContractTypeId) : null
        };
    }
    parseCalendarEvents(data) {
        return data.map((event) => this.virtualEventToCalendarEvent(event, false)) || [];
    }
    virtualEventToCalendarEvent(event, isLeave) {
        return {
            Id: event.RelatedIds?.[0] || -1,
            Period: {
                StartDate: event.Start,
                EndDate: event.End || null
            },
            WorkShift: event.WorkShift || {},
            Department: event.Department || {},
            Errors: null,
            ProjectId: event.ProjectId || null,
            Comment: event.Comment || '',
            Flags: event.Flags ? [event.Flags] : null,
            FixedCommentId: event.FixedComment?.Id || null,
            FixedComment: event.FixedComment?.Name || '',
            IncludesPaidLunch: null,
            LunchLength: null,
            LunchEventId: null,
            Job: {
                Id: event.Job?.Id || 0,
                Name: event.Job?.Name || '',
                ExternalCode: event.Job?.ExternalId || '',
                // @ts-ignore
                JobColor: event.Job?.JobProfile?.ColorStart || '#000000',
                IsLeave: isLeave,
                Period: {
                    StartDate: event.Start,
                    EndDate: event.End || null
                }
            }
        };
    }
    parsePlannedEvents(data) {
        const plannedWorks = data.PlannedVirtualEvents?.map((event) => this.virtualEventToPlannedEvent(event, false)) || [];
        const plannedAbsences = data.Absences?.map((absence) => this.virtualEventToPlannedEvent(absence, true)) || [];
        const allPlannings = [...plannedAbsences, ...plannedWorks];
        return allPlannings.reduce((acc, event) => {
            const startDate = dayjs(event.StartDateTime);
            const endDate = dayjs(event.EndDateTime);
            const nextMidnight = startDate.add(1, 'day').startOf('day');
            // If event doesn't cross midnight, add it as is
            if (endDate.isSameOrBefore(nextMidnight)) {
                acc.push(event);
                return acc;
            }
            const nextMidnightStr = nextMidnight.format(WTS_MOMENT_DATE_MASK);
            // Split event into two parts at midnight
            const firstEvent = {
                ...event,
                EndDateTime: nextMidnightStr,
                WorkHours: utils.time.durationBetweenDates(event.StartDateTime, nextMidnightStr)
            };
            const secondEvent = {
                ...event,
                StartDateTime: nextMidnightStr,
                WorkHours: utils.time.durationBetweenDates(nextMidnightStr, event.EndDateTime)
            };
            acc.push(firstEvent, secondEvent);
            return acc;
        }, []);
    }
    virtualEventToPlannedEvent(event, isLeave) {
        return {
            Id: event.RelatedIds?.[0] || -1,
            StartDateTime: event.Start,
            EndDateTime: event.End,
            ActualStartDateTime: event.Start,
            ActualEndDateTime: event.End,
            DepartmentName: event.Department?.Name || '',
            WorkShiftName: event.WorkShift?.Name || '',
            WorksiteName: null,
            EventName: event.Job?.Name || '',
            WorkHours: utils.time.durationBetweenDates(event.Start, event.End || dayjs().toString()),
            IsLeave: isLeave,
            JobProfileId: event.Job?.JobProfile?.Id || 0,
            JobColorStart: event.Job?.JobProfile?.ColorStart || '#000000',
            Breaks: null,
            JobExternalCode: event.Job?.ExternalId || '',
            JobShortName: event.Job?.ShortName || '',
            Confirmations: null,
            LunchEvent: undefined,
            EventMetas: null,
            UserId: event.UserId || 0,
            WorksiteId: null,
            WorkShiftId: event.WorkShift?.Id || null,
            DepartmentId: event.Department?.Id,
            JobId: !isLeave ? event.Job?.Id : null,
            LeaveId: isLeave ? event.Job?.Id : null,
            Comments: event.Comment || '',
            StartOverTime: event.StartOverTime || null,
            EndOverTime: event.EndOverTime || null,
            EventOverTimeLocation: 'all',
            CreatorId: null,
            IsRestricted: null,
            ImportCode: undefined,
            CreatedDt: undefined,
            ModifiedDt: undefined,
            ModifierId: undefined,
            Flags: event.Flags || undefined,
            Source: undefined,
            IncludesPaidLunch: undefined,
            LunchLength: undefined,
            // @ts-ignore
            Period: {
                StartDate: event.Start,
                EndDate: event.End
            }
        };
    }
    parseJobs(plannedEvents, // Planned
    calendarEvents, // Worked
    calculatedEvents // Calculated
    ) {
        const jobs = this.period.reduce((counter, day) => ({
            ...counter,
            [day]: {
                Period: {
                    StartDate: day,
                    EndDate: dayjs(day).endOf('day').format(WTS_MOMENT_DATE_MASK)
                },
                Planned: [],
                Worked: [],
                Calculated: []
            }
        }), {});
        plannedEvents.forEach((event) => {
            const key = `${event.StartDateTime.split('T')[0]}T00:00:00`;
            jobs[key]?.Planned?.push({
                ...event,
                Period: {
                    StartDate: event.StartDateTime,
                    EndDate: event.EndDateTime
                }
            });
        });
        calendarEvents.forEach((event) => {
            this.addEventWithSplitting(event, jobs, 'Worked');
        });
        calculatedEvents.forEach((event) => {
            this.addEventWithSplitting(event, jobs, 'Calculated');
        });
        return jobs;
    }
    addEventWithSplitting(event, jobs, eventType) {
        if (!event.Period?.StartDate)
            return;
        const startDate = dayjs(event.Period.StartDate);
        const endDate = dayjs(event.Period.EndDate || undefined);
        const isUnfinished = !event.Period.EndDate;
        const startDateKey = startDate.startOf('day').format(WTS_MOMENT_DATE_MASK);
        const endDateKey = endDate.endOf('day').format(WTS_MOMENT_DATE_MASK);
        // Single day event
        if (startDateKey.split('T')[0] === endDateKey.split('T')[0]) {
            const eventWithFinished = {
                ...event,
                unFinished: isUnfinished
            };
            const dayJob = jobs[startDateKey];
            if (dayJob && dayJob[eventType]) {
                dayJob[eventType].push(eventWithFinished);
            }
            return;
        }
        // Split event across multiple days
        let currentDate = startDate;
        let isFirstPart = true;
        while (currentDate.format(WTS_MOMENT_DATE_MASK) <= endDateKey) {
            const currentDateKey = currentDate.startOf('day').format(WTS_MOMENT_DATE_MASK);
            const isLastPart = currentDate.format(WTS_MOMENT_SHORT_DATE_MASK) === endDateKey.split('T')[0];
            // Calculate period for this segment ensuring EndDate doesn't exceed 23:59:59 of the day
            const segmentStartDate = isFirstPart ? startDate : currentDate.startOf('day');
            const segmentEndDate = isLastPart ? endDate : currentDate.endOf('day');
            const splitEvent = {
                ...event,
                Period: {
                    ...event.Period,
                    StartDate: segmentStartDate.format(WTS_MOMENT_DATE_MASK),
                    EndDate: segmentEndDate.format(WTS_MOMENT_DATE_MASK)
                },
                SplitEventData: {
                    initialPeriod: {
                        StartDate: event.Period.StartDate,
                        EndDate: event.Period.EndDate
                    },
                    isFirstPart,
                    isLastPart
                },
                unFinished: isUnfinished
            };
            const dayJob = jobs[currentDateKey];
            if (dayJob && dayJob[eventType]) {
                dayJob[eventType].push(splitEvent);
            }
            currentDate = currentDate.add(1, 'day');
            isFirstPart = false;
        }
    }
    parseErrors() {
        const errors = {};
        this.period.forEach((timeStamp) => {
            errors[timeStamp] = [];
        });
        return errors;
    }
    parsePersonTotals(workedData, calculatedData) {
        const totals = {
            RegisteredTotal: this.getTimeSpan(workedData.ActualEventsTotals?.Total),
            DaysOfAbsences: workedData.Absences?.length || 0,
            AbsenceD: workedData.Absences?.length || 0,
            CalculatedTotal: this.getTimeSpan(calculatedData.ActualWorkTotals?.Total),
            PlannedNormalWorkTime: this.getTimeSpan(workedData.PlannedStandardHours),
            PlannedOverTime: this.getTimeSpan(workedData.PlannedOverUnder),
            PlannedTotalTime: this.getTimeSpan(workedData.PlannedEventsTotals?.Total),
            AbsenceH: this.getTimeSpan(workedData.AbsenceTotals?.Total),
            UnscheduledWork: this.getTimeSpan(workedData.ActualOffScheduleWorkTotals?.Total),
            WatchTime: this.getTimeSpan(workedData.ActualOutsideStandardTotals?.Total),
            Lunch: this.getTimeSpan(workedData.ActualLunchesTotals?.Total),
            WorkTime: this.getTimeSpan(workedData.ActualStandardWorkTotals?.Total),
            OverUnderTime: this.getTimeSpan(workedData.ActualOverUnder),
            OverUnderHours: utils.time.timeSpanToHoursDecimal(workedData.ActualOverUnder || '0'),
            NormHours: this.getTimeSpan(workedData.ActualStandardHours),
            TransferredFreeTime: this.getTimeSpan(workedData.TransferredTime),
            UsedTransferedTime: this.getTimeSpan(workedData.UsedTransferredTime),
            PlannedTransferedTime: this.getTimeSpan(workedData.PlannedTransferredTime)
        };
        return totals;
    }
    parseUserContracts(data) {
        const contracts = [];
        let activeContract = null;
        data.Contracts?.forEach((contract) => {
            const candidate = {
                UserId: data.Id,
                ContractTypeId: contract.ContractTypeId,
                ContractRows: contract.ContractRows?.map((row) => ({
                    AssociatedJobs: [],
                    Department: {
                        DepartmentId: row.Department?.Id,
                        DepartmentName: row.Department?.Name
                    },
                    Profession: {
                        Id: row.Profession?.Id,
                        Name: row.Profession?.Name,
                        ExternalId: row.Profession?.ExternalId
                    },
                    Errors: null,
                    ExternalCode: row.ExternalId,
                    Id: row.Id,
                    IsDefault: row.IsDefault,
                    Name: row.Name,
                    ProportionPercent: row.ProportionPercent,
                    UserContractId: contract.Id
                })) || [],
                ExternalCode: contract.ExternalId,
                Id: contract.Id,
                Name: contract.Name,
                Period: {
                    StartDate: contract.Active?.Start,
                    EndDate: contract.Active?.End || null
                },
                TimeRateAssociation: {
                    Id: contract.TimeRateSystem?.Id,
                    PeriodStart: contract.TimeRateSystem?.Start,
                    PeriodLength: contract.TimeRateSystem?.Diameter,
                    TimeRateSystemTypeId: utils.strings.decapitalize(contract.TimeRateSystem?.Name || '')
                },
                User: {
                    Id: data.Id,
                    FirstName: data.Firstname,
                    LastName: data.Lastname,
                    ExternalCode: data.ExternalCode,
                    ExternalCode2: data.ExternalCode2
                }
            };
            if (!candidate.Period.EndDate) {
                activeContract = candidate;
            }
            contracts.push(candidate);
        });
        return {
            contracts,
            activeContract
        };
    }
    getTimeSpan(timeSpan) {
        return utils.time.timeSpanWithDaysToUsual(timeSpan || '00:00:00');
    }
}
